/**
 * Work Requests Service - API calls for work requests management
 */

import { getFileUrl } from './filesService';

const API_BASE = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be';

/**
 * En-têtes d'un appel qui écrit.
 *
 * Le serveur vérifie désormais que la demande visée appartient bien à
 * l'appelant. Il lui faut donc une identité prouvée : le JWT signé au login,
 * et non le `userId` que le corps de la requête déclarait jusqu'ici — ce
 * dernier venait du client, et se remplaçait.
 */
function enTetesEcriture(avecCorps = true) {
    let token = null;
    try {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch {
        token = null;
    }

    const headers = {};
    if (avecCorps) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

/**
 * Request types with colors
 */
export const REQUEST_TYPES = {
    job_fulltime: { label: 'Plein-temps', color: '#3b82f6' },      // blue
    job_parttime: { label: 'Mi-temps', color: '#60a5fa' },          // blue-light
    job_hours: { label: 'Quelques heures', color: '#06b6d4' },      // cyan
    home_service: { label: 'Service à domicile', color: '#22c55e' }, // green
    project: { label: 'Pour un projet', color: '#8b5cf6' },          // violet
    family: { label: 'Pour la famille', color: '#f97316' },          // orange
    friend: { label: 'Pour un ami', color: '#ec4899' },              // pink
};

/**
 * Urgency badges with colors and sort order
 */
export const URGENCY_BADGES = {
    urgent: { label: 'Urgent', color: '#ef4444', order: 1 },         // red
    this_week: { label: 'Cette semaine', color: '#f97316', order: 2 }, // orange
    asap: { label: 'Dès que possible', color: '#8b5cf6', order: 3 },  // violet
    this_month: { label: 'Ce mois-ci', color: '#eab308', order: 4 },  // yellow
    three_months: { label: 'Dans 3 mois', color: '#84cc16', order: 5 }, // lime
    six_months: { label: 'Dans 6 mois', color: '#06b6d4', order: 6 },  // cyan
    this_year: { label: 'Dans l\'année', color: '#6b7280', order: 7 }, // gray
};

/**
 * Compensation types with colors
 */
export const COMPENSATION_TYPES = {
    paid: { label: 'Rémunéré', color: '#22c55e' },        // green
    exchange: { label: 'Échange', color: '#f59e0b' },     // amber
    volunteer: { label: 'Bénévolat', color: '#ec4899' },  // pink
};

/**
 * Duration types with colors
 */
export const DURATION_TYPES = {
    hour: { label: 'Quelques heures', color: '#60a5fa' },    // blue-light
    days: { label: 'Quelques jours', color: '#3b82f6' },     // blue
    months: { label: 'Quelques mois', color: '#8b5cf6' },    // violet
    year: { label: 'Un an ou plus', color: '#6366f1' },      // indigo
    undefined: { label: 'Indéterminé', color: '#6b7280' },   // gray
};

/**
 * Get all public work requests
 * @param {Object} filters - Filters (type, urgency, search, sortBy, sortDir)
 * @returns {Promise<Array>} List of requests
 */
export const getPublicRequests = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        if (filters.type) params.append('type', filters.type);
        if (filters.urgency) params.append('urgency', filters.urgency);
        if (filters.search) params.append('search', filters.search);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDir) params.append('sortDir', filters.sortDir);

        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests?${params}`);
        if (!response.ok) throw new Error('Failed to fetch requests');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const requests = data.data ?? data.DATA ?? [];
        return success ? requests : [];
    } catch (error) {
        console.error('Error fetching public requests:', error);
        return [];
    }
};

/**
 * Get user's own requests
 * @param {number} userId - User ID
 * @returns {Promise<Array>} List of user's requests
 */
export const getMyRequests = async (userId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/my?userId=${userId}`, {
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch my requests');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const requests = data.data ?? data.DATA ?? [];
        return success ? requests : [];
    } catch (error) {
        console.error('Error fetching my requests:', error);
        return [];
    }
};

/**
 * Get single request by ID
 * @param {number} requestId - Request ID
 * @returns {Promise<Object|null>} Request data or null
 */
export const getRequestById = async (requestId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}`);
        if (!response.ok) throw new Error('Failed to fetch request');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const request = data.data ?? data.DATA ?? null;
        return success ? request : null;
    } catch (error) {
        console.error('Error fetching request:', error);
        return null;
    }
};

/**
 * Create a new work request
 * @param {Object} requestData - Request data
 * @returns {Promise<Object>} Result with success status
 */
export const createRequest = async (requestData) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify(requestData)
        });
        const result = await response.json();
        // Normalize ColdFusion uppercase keys
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE,
            requestId: result.requestId ?? result.REQUESTID
        };
    } catch (error) {
        console.error('Error creating request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a work request
 * @param {number} requestId - Request ID
 * @param {Object} requestData - Update data
 * @returns {Promise<Object>} Result with success status
 */
export const updateRequest = async (requestId, requestData) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify(requestData)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a work request
 * L'identité vient du JWT côté serveur ; il n'est plus nécessaire de passer
 * le userId ici.
 * @param {number} requestId - Request ID
 * @returns {Promise<Object>} Result with success status
 */
export const deleteRequest = async (requestId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}`, {
            method: 'DELETE',
            headers: enTetesEcriture(false)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Close a work request
 * L'identité vient du JWT côté serveur ; il n'est plus nécessaire de passer
 * le userId ici.
 * @param {number} requestId - Request ID
 * @returns {Promise<Object>} Result with success status
 */
export const closeRequest = async (requestId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/close`, {
            method: 'POST',
            headers: enTetesEcriture()
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error closing request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Publish a work request - notify selected Cabaza profiles
 * @param {number} requestId - Request ID
 * @param {number[]} cabazaProfileIds - Array of people IDs to notify
 * @param {boolean} showPhone - Whether to show creator's phone number
 * @returns {Promise<Object>} Result with success status, notifiedCount, emailsSentCount
 */
export const publishRequest = async (requestId, cabazaProfileIds, showPhone) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/publish`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ cabazaProfileIds, showPhone })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE,
            notifiedCount: result.notifiedCount ?? result.NOTIFIEDCOUNT ?? 0,
            emailsSentCount: result.emailsSentCount ?? result.EMAILSSENTCOUNT ?? 0
        };
    } catch (error) {
        console.error('Error publishing request:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get type color
 * @param {string} type - Request type
 * @returns {string} Color hex
 */
export const getTypeColor = (type) => REQUEST_TYPES[type]?.color || '#6b7280';

/**
 * Get type label
 * @param {string} type - Request type
 * @returns {string} Label
 */
export const getTypeLabel = (type) => REQUEST_TYPES[type]?.label || type;

/**
 * Get urgency color
 * @param {string} urgency - Urgency badge
 * @returns {string} Color hex
 */
export const getUrgencyColor = (urgency) => URGENCY_BADGES[urgency]?.color || '#6b7280';

/**
 * Get urgency label
 * @param {string} urgency - Urgency badge
 * @returns {string} Label
 */
export const getUrgencyLabel = (urgency) => URGENCY_BADGES[urgency]?.label || urgency;

/**
 * Get compensation color
 * @param {string} compensation - Compensation type
 * @returns {string} Color hex
 */
export const getCompensationColor = (compensation) => COMPENSATION_TYPES[compensation]?.color || '#6b7280';

/**
 * Get compensation label
 * @param {string} compensation - Compensation type
 * @returns {string} Label
 */
export const getCompensationLabel = (compensation) => COMPENSATION_TYPES[compensation]?.label || compensation;

/**
 * Get duration color
 * @param {string} duration - Duration type
 * @returns {string} Color hex
 */
export const getDurationColor = (duration) => DURATION_TYPES[duration]?.color || '#6b7280';

/**
 * Get duration label
 * @param {string} duration - Duration type
 * @returns {string} Label
 */
export const getDurationLabel = (duration) => DURATION_TYPES[duration]?.label || duration;

/**
 * Get occupations for a work request
 * @param {number} requestId - Request ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of occupations
 */
export const getRequestOccupations = async (requestId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/occupations?lang=${lang}`);
        if (!response.ok) throw new Error('Failed to fetch occupations');
        const data = await response.json();
        return data.success ? data.data : [];
    } catch (error) {
        console.error('Error fetching request occupations:', error);
        return [];
    }
};

/**
 * Get skills for a work request
 * @param {number} requestId - Request ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of skills
 */
export const getRequestSkills = async (requestId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/skills?lang=${lang}`);
        if (!response.ok) throw new Error('Failed to fetch skills');
        const data = await response.json();
        return data.success ? data.data : [];
    } catch (error) {
        console.error('Error fetching request skills:', error);
        return [];
    }
};

/**
 * Get creator profile with photo by peopleUuid
 * @param {string} peopleUuid - Creator's UUID
 * @returns {Promise<Object>} Profile data with photo
 */
export const getCreatorProfile = async (peopleUuid) => {
    try {
        // Get profile info
        const profileResponse = await fetch(`${API_BASE}/rest/cabaza/peoples/${peopleUuid}`);
        if (!profileResponse.ok) throw new Error('Failed to fetch profile');
        const profileData = await profileResponse.json();

        // Get profile photo
        let photoUrl = null;
        try {
            const photoResponse = await fetch(`${API_BASE}/rest/cabaza/files/profile-photo/${peopleUuid}`);
            if (photoResponse.ok) {
                const photoData = await photoResponse.json();
                const photoSuccess = photoData.success ?? photoData.SUCCESS;
                // L'API renvoie un objet unique dans DATA, jamais un tableau `files`.
                // Sans photo, DATA vaut {} : filePath est alors absent.
                const photo = photoData.data ?? photoData.DATA ?? {};
                const filePath = photo.filePath ?? photo.filepath ?? photo.FILEPATH;
                if (photoSuccess && filePath) {
                    photoUrl = getFileUrl(filePath);
                }
            }
        } catch (photoError) {
            console.log('No profile photo found');
        }

        const profileSuccess = profileData.success ?? profileData.SUCCESS;
        const profile = profileData.data ?? profileData.DATA ?? null;

        return {
            success: true,
            profile: profileSuccess ? profile : null,
            photoUrl
        };
    } catch (error) {
        console.error('Error fetching creator profile:', error);
        return { success: false, profile: null, photoUrl: null };
    }
};

// ============================================
// Dashboard — Recipients, Stats, Prospection
// ============================================

/**
 * Get all recipients for a work request (Cabaza + Google Places)
 * @param {number} requestId - Request ID
 * @returns {Promise<Array>} List of recipients
 */
export const getRecipients = async (requestId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/recipients`, {
            headers: enTetesEcriture(false)
        });
        if (!response.ok) throw new Error('Failed to fetch recipients');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const recipients = data.data ?? data.DATA ?? [];
        return success ? recipients : [];
    } catch (error) {
        console.error('Error fetching recipients:', error);
        return [];
    }
};

/**
 * Get stats summary for a work request (views, responses, emails)
 * @param {number} requestId - Request ID
 * @returns {Promise<Object>} Stats object
 */
export const getRequestStats = async (requestId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/stats`, {
            headers: enTetesEcriture(false)
        });
        if (!response.ok) throw new Error('Failed to fetch stats');
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        const stats = data.data ?? data.DATA ?? {};
        return success ? stats : {};
    } catch (error) {
        console.error('Error fetching request stats:', error);
        return {};
    }
};

/**
 * Launch Google Maps prospection (save selected pros + trigger n8n)
 * @param {number} requestId - Request ID
 * @param {Array} googlePlaces - Selected places [{ name, city, occupation, phone, website }]
 * @param {string} sessionId - SSE session ID for real-time updates
 * @returns {Promise<Object>} { success, prospectCount, n8nTriggered }
 */
export const prospectRequest = async (requestId, googlePlaces, sessionId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/prospect`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ googlePlaces, sessionId })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE,
            prospectCount: result.prospectCount ?? result.PROSPECTCOUNT ?? 0,
            n8nTriggered: result.n8nTriggered ?? result.N8NTRIGGERED ?? false
        };
    } catch (error) {
        console.error('Error launching prospection:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update contact status for a recipient (manual tracking by creator)
 * @param {number} requestId - Request ID
 * @param {number} recipientId - Recipient ID
 * @param {Object} data - { contactedByPhone, contactedByEmail, notes }
 * @returns {Promise<Object>} { success, message }
 */
export const updateContactStatus = async (requestId, recipientId, data) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/recipients/${recipientId}/contact-status`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify(data)
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating contact status:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Mark a work request as viewed by a cabazat
 * @param {number} requestId - Request ID
 * L'identité vient du JWT côté serveur ; il n'est plus nécessaire de passer
 * le peopleId ici.
 * @returns {Promise<Object>} { success }
 */
export const viewRequest = async (requestId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/view`, {
            method: 'PUT',
            headers: enTetesEcriture()
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS
        };
    } catch (error) {
        console.error('Error marking request as viewed:', error);
        return { success: false };
    }
};

/**
 * Respond to a work request (cabazat: interested / not interested)
 * L'identité vient du JWT côté serveur ; il n'est plus nécessaire de passer
 * le peopleId ici — on répond toujours en son propre nom.
 * @param {number} requestId - Request ID
 * @param {string} responseType - 'interested' or 'not_interested'
 * @param {string} message - Optional message
 * @returns {Promise<Object>} { success, message }
 */
export const respondToRequest = async (requestId, responseType, message = '') => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/work-requests/${requestId}/respond`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ response: responseType, message })
        });
        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS,
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error responding to request:', error);
        return { success: false, message: error.message };
    }
};
